"use client";

import SectionTitle from "@/shared/app/ui/text/PageSectionTitle";
import { motion } from "framer-motion";
import Image from "next/image";
interface SubItem {
  label: string;
  text: string;
}

interface Item {
  number: string | number;
  title: string;
  details: SubItem[];
}

interface Props {
  items: Item[];
}

/* -------------------- FlowList -------------------- */
const SynergyList: React.FC<Props> = ({ items }) => {
  return (
    <ul className="grid grid-cols-1 lg:grid-cols-3 md:grid-cols-2 gap-6 mt-20">
      {items.map((item, i) => (
        <motion.li
          key={i}
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: i * 0.2 }}
          viewport={{ once: true, amount: 0.3 }}
          className="bg-white rounded-3xl md:rounded-4xl shadow-2xs border border-light-50 xl:py-14 xl:px-10 md:py-10 py-8 px-5 flex flex-col gap-2 md:gap-4 hover:shadow-md hover:-translate-y-1 transition-all duration-300"
        >
          {/* 상단 번호 + 타이틀 */}
          <span
            className="md:w-8 md:h-8 w-6 h-6 flex justify-center items-center rounded-full bg-[#3381FF] text-white  md:text-lg text-sm"
            style={{ fontFamily: '"pre-Medium", sans-serif' }}
          >
            0{item.number}
          </span>
          <p
            className="md:text-2xl text-lg tracking-tight text-black-200 md:mt-10 mt-1"
            style={{ fontFamily: '"one-b", sans-serif' }}
          >
            {item.title}
          </p>

          {/* 하위 리스트 */}
          <ul className="flex flex-col md:gap-5 gap-2 mt-2 md:mt-4">
            {item.details.map((sub, idx) => (
              <li key={idx} className="flex items-center gap-2 md:gap-3">
                <span
                  className="md:text-base text-sm md:min-w-[90px] min-w-[70px] bg-[#D7EAF9] flex justify-center items-center text-[#3381FF] tracking-tighter leading-6 py-0.5"
                  style={{ fontFamily: '"one-r", sans-serif' }}
                >
                  {sub.label}
                </span>
                <p
                  className="text-midGray-40 md:text-base text-sm tracking-tight leading-6"
                  style={{ fontFamily: '"one-r", sans-serif' }}
                >
                  {sub.text}
                </p>
              </li>
            ))}
          </ul>
        </motion.li>
      ))}
    </ul>
  );
};

const data: Item[] = [
  {
    number: 1,
    title: "사회적기업",
    details: [
      { label: "사업신청", text: "사회적기업 행정서류 관리" },
      { label: "사회서비스", text: "시회적성과관리" },
      { label: "지원금내역", text: "재정지원사업비 조회" },
    ],
  },
  {
    number: 2,
    title: "인사",
    details: [
      { label: "직원명부", text: "직원별 인사정보 관리" },
      { label: "근태대장", text: "출근부, 연차대장" },
      { label: "급여대장", text: "급여대장 관리" },
    ],
  },
  {
    number: 3,
    title: "회계",
    details: [
      { label: "수입지출", text: "회계관리 및 보고서 출력" },
      { label: "기초정보", text: "거래처, 계정과목 등 관리" },
      { label: "계약관리", text: "계약 및 과업현황 관리" },
    ],
  },
  {
    number: 4,
    title: "협동조합",
    details: [
      { label: "조합원", text: "협동조합 조합원 정보 관리" },
      { label: "출자금", text: "조합원별 출자금 현황 관리" },
      { label: "기타", text: "후원금 관리" },
    ],
  },
  {
    number: 5,
    title: "공제회",
    details: [
      { label: "회원", text: "가입 및 회비, 대출금 개별 관리" },
      { label: "공제금", text: "회원별 회비, 대출금 관리" },
      { label: " 누적결산", text: "공제금 현금 흐름 관리" },
    ],
  },
  {
    number: 6,
    title: "관리자",
    details: [
      { label: "권한", text: "직원별 접근권한 설정" },
      { label: "직원", text: "직원 관리 및 계정 생성" },
      { label: "결제", text: "사내문서 결제권한 설정" },
    ],
  },
];
const items = [
  {
    id: 1,
    src: "/assets/images/page/service/solution/web1.png",
    title: "사회적경제연구원 그룹웨어",
    subtitle: "Groupware System",
  },
  {
    id: 2,
    src: "/assets/images/page/service/solution/web2.png",
    title: "공익활동가 서로도움 공제회",
    subtitle: "Cooperative Association",
  },
  {
    id: 3,
    src: "/assets/images/page/service/solution/web3.png",
    title: "우리랑 사회적협동조합",
    subtitle: "Social Cooperative",
  },
];
export default function SolutionSynergySection() {
  return (
    <div className="bg-[#FAFBFF] rounded-3xl md:rounded-[154px] my-5 md:my-20">
      <div className="w-full xl:max-w-[1280px] mx-auto px-5 xl:px-2 md:py-50 py-20">
        <SectionTitle
          align="left"
          label="Synergy"
          title={
            <>
              사회적경제 특화 그룹웨어,
              <br />
              공제회 시스템
            </>
          }
          description={
            <>
              다양한 문제를 해결하고 있는 기업들과
              <br />
              필요한 IT 비용 마련을 위한 컨설팅 및 협업
            </>
          }
          colorMode="light"
        />

        {/* 리스트 시작 */}
        <SynergyList items={data} />
        {/* 리스트 끝 */}
        {/* 아이콘 */}
        <motion.div
          className="w-16 h-20 relative overflow-hidden mx-auto my-20"
          animate={{
            y: [0, 10, 0], // 위→아래→원위치
          }}
          transition={{
            duration: 1.2, // 한 사이클 시간
            ease: "easeInOut",
            repeat: Infinity, // 계속 반복
          }}
        >
          <Image
            src="/assets/images/page/service/solution/downArrow.svg"
            alt="arrow"
            fill
            className="object-contain"
          />
        </motion.div>

        {/* 홈페이지 리스트 */}
        <ul className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-6 w-full  mx-auto mt-16">
          {items.map((item, i) => (
            <motion.li
              key={item.id}
              className="flex flex-col overflow-hidden"
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease: "easeOut", delay: i * 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              {/* 이미지 영역 */}
              <div className="relative w-full aspect-[446/300]  md:aspect-[446/328] rounded-2xl overflow-hidden shadow-2xs border border-light-10">
                <Image
                  src={item.src}
                  alt={item.title}
                  fill
                  className="object-cover transition-transform duration-500 hover:scale-105"
                />
              </div>

              {/* 텍스트 영역 */}
              <div className="md:pt-6 pt-4 px-2 md:px-5 flex flex-col items-start md:gap-1 gap-0.5">
                <p
                  className="md:text-lg text-base text-black-100 tracking-tight"
                  style={{ fontFamily: '"pre-SemiBold", sans-serif' }}
                >
                  {item.title}
                </p>
                <p
                  className="text-sm text-lightGray-20 tracking-tight"
                  style={{ fontFamily: '"pre-Regular", sans-serif' }}
                >
                  {item.subtitle}
                </p>
              </div>
            </motion.li>
          ))}
        </ul>
      </div>
    </div>
  );
}
